<template>
  <div v-if="isLoading" class="menu-fullscreen-loading">
    <loading>Loading...</loading>
  </div>
  <div v-else class="project-view">
    <div class="project-view-left">
      <section class="section settings">
        <legend>Settings</legend>
        <div class="section-sub">
          <label>Name</label>
          <input class="input" type="text" v-model="name" />
        </div>
        <div class="section-sub">
          <label>Tempo</label>
          <input class="input" type="number" v-model="tempo" />
        </div>
        <div class="section-sub buttons">
          <button @click.prevent.stop="saveSettings" class="btn-outline">
            Save Settings
          </button>
        </div>
      </section>
      <section class="section mixes">
        <legend>Mixes</legend>
        <div
          v-for="mix in project.mixes"
          :key="mix._id"
          class="section-sub project-mix"
        >
          <div class="mix-name">
            <router-link :to="`/mixes/${mix._id}`">
              {{ project.name }} Mix v{{ mix.version }}
            </router-link>
            by {{ mix.author.first_name }} {{ mix.author.last_name }}
          </div>
          <div class="mix-description">
            {{ mix.description || "no description" }}
          </div>
        </div>
        <div v-if="project.mixes.length === 0" class="section-sub mixes-empty">
          <div>No mixes yet</div>
        </div>
        <div class="section-sub buttons">
          <button @click.prevent.stop="createMix" class="btn-outline">
            Create new
          </button>
        </div>
      </section>
      <section class="section danger">
        <legend>Danger</legend>
        <div class="section-sub buttons">
          <button
            @click.prevent.stop="deleteConfirm = true"
            class="btn-outline btn-danger"
          >
            Destroy Project
          </button>
        </div>
      </section>
    </div>
    <div class="project-view-right">
      <section class="section tracks">
        <legend>Tracks</legend>
        <file-drop @files="onDropFiles" />
        <div class="project-view-track-list">
          <progress-track-list-item
            v-for="track in progressTracks"
            :key="track.name"
            :track="track"
          />
          <track-list-item
            v-for="track in project.tracks"
            :key="track._id"
            :track="track"
            @delete="onDeleteTrack"
          />
        </div>
      </section>
    </div>
  </div>
  <confirm
    title="Destroy Project"
    v-if="deleteConfirm"
    @ok="destroyProject"
    @click="deleteConfirm = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import Loading from "@/components/Loading.vue";
import { mapState } from "vuex";
import { Project, Track } from "@/models";
import FileDrop from "@/components/FileDrop.vue";
import TrackListItem from "@/components/TrackListItem.vue";
import ProgressTrackListItem from "@/components/ProgressTrackListItem.vue";
import { ProgressTrack } from "@/store/projects";
import Confirm from "@/components/Confirm.vue";

@Options({
  components: {
    Confirm,
    Loading,
    FileDrop,
    TrackListItem,
    ProgressTrackListItem,
  },
  data() {
    return {
      isLoading: true,
      deleteConfirm: false,
      name: "",
      tempo: 0,
    };
  },
  computed: {
    ...mapState("projects", {
      project: "current",
      progressTracks: "progressTracks",
    }),
  },
  watch: {
    "$route.params"() {
      if (this.$route.params.id) {
        this.reload();
      }
    },
  },
})
export default class ProjectView extends Vue {
  isLoading!: boolean;
  project!: Project;
  progressTracks!: ProgressTrack[];
  deleteConfirm!: boolean;

  name!: string;
  tempo!: number;

  reload() {
    this.isLoading = true;
    this.$store
      .dispatch("projects/loadCurrent", this.$route.params.id)
      .then(() => {
        this.pullProjectFields();
        this.isLoading = false;
        document.title = `${this.project.name} by ${this.project.band.name} - Eavesdrop`;
      });
  }

  private pullProjectFields() {
    this.name = this.project.name;
    this.tempo = this.project.tempo;
  }

  onDropFiles(files: File[]) {
    this.$store.dispatch("projects/uploadFiles", files);
  }

  onDeleteTrack(track: Track) {
    this.$store.dispatch("projects/deleteTrack", track);
  }

  mounted() {
    this.reload();
  }

  destroyProject() {
    this.$store
      .dispatch("projects/destroyProject", this.project._id)
      .then(() => {
        this.$router.replace("/projects");
      });
  }

  saveSettings() {
    this.$store
      .dispatch("projects/updateProject", {
        name: this.name,
        tempo: this.tempo,
      })
      .then(() => {
        this.pullProjectFields();
      });
  }

  createMix() {
    this.$store.dispatch("projects/createMix").then((mix) => {
      this.$router.push(`/mixes/${mix._id}`);
    });
  }
}
</script>

<style lang="scss">
.project-view {
  background: #d0d0d0;
  min-height: calc(100vh - 24px);
  border-top: 1px solid #999;
  display: flex;
  padding: 12px;

  .project-view-left {
    width: 350px;
    margin-right: 1em;
  }
  .project-view-right {
    flex-grow: 1;
  }
}

section.section {
  border: 1px solid #999;
  padding: 16px;
  border-radius: 4px;
  position: relative;
  margin-bottom: 1em;
  legend {
    position: absolute;
    top: -8px;
    left: 12px;
    background: #d0d0d0;
    padding: 0 6px;
  }
}

.section-sub {
  &:not(:last-child) {
    margin-bottom: 8px;
  }

  label {
    display: block;
    font-size: 0.9em;
    margin-bottom: 1px;
  }
  input[type="text"] {
    width: 100%;
  }
  input[type="number"] {
    width: 56px;
  }

  &.buttons {
    margin-top: 1em;
    text-align: right;
  }
}

.mix-name {
  font-size: 1.1em;
  a {
    font-weight: bold;
    color: black;
  }
}

.mix-description {
  font-size: 1em;
  font-style: italic;
}
</style>
